//
// Material-graph node registry. One entry per node type: its inputs, outputs,
// defaults and editor metadata. Pure DATA — no THREE, no DOM, and no shader
// source: what a node COMPUTES lives in `TSL_NODES` (materialGraphTSL.js), the
// one compiler, and this table is what the editor and the graph walk read.
//
// Every entry used to carry an `emit(ctx)` returning a GLSL statement, for a
// second compiler that is deleted.
//
// There is deliberately no exported SURFACE_SLOTS list. One used to sit here,
// claiming the output node's inputs "map 1:1 to the surface slots the compiler
// injects" — and it was wrong: it never listed `specular`, and it could not
// list `clearcoat`/`clearcoatRoughness`. Nothing in this repo read it, and its
// only consumer was aos-mcp's generated node catalog, so a stale hand-kept list
// was being shipped to the agent as the compiler's contract. What an output
// node accepts is its own `inputs` below — derived, not restated.

// Noise `type` enum -> fractal helper name. All share the signature
// fn(vec2 p, detail, roughness, lacunarity, offset, gain, normalize).
const NOISE_FRACTAL_FN = {
  fbm:          'aos_fbm2',
  multifractal: 'aos_multi_fractal2',
  hetero:       'aos_hetero_terrain2',
  ridged:       'aos_ridged_multi_fractal2',
  hybrid:       'aos_hybrid_multi_fractal2',
}

export const NODE_TYPES = {
  PrincipledBSDF: {
    label: 'Principled BSDF',
    isOutput: true,
    inputs: [
      { handle: 'color',         type: 'vec3',  default: [1, 1, 1] },
      { handle: 'metalness',     type: 'float', default: 0 },
      { handle: 'roughness',     type: 'float', default: 0.5 },
      // Specular = dielectric reflectance, 0..1 (Unreal's Specular / Blender's
      // "Specular IOR Level"). F0 = 0.08 * specular, so the default 0.5 == three's
      // 4% — leaving existing graphs unchanged. No specular-color/tint (Unreal has
      // none; metals get their tint from base color via metalness).
      { handle: 'specular',      type: 'float', default: 0.5 },
      { handle: 'normal',        type: 'vec3',  default: null },   // null => leave three's normal
      { handle: 'emissive',      type: 'vec3',  default: [0, 0, 0] },
      { handle: 'opacity',       type: 'float', default: 1 },
      { handle: 'ao',            type: 'float', default: 1 },
      // Clearcoat: a second, thin specular lobe over the base surface — car
      // paint, varnish, and the wet film on an eye. OPT-IN (`optInSlot`): three
      // compiles the clearcoat code only under USE_CLEARCOAT, which it gates on
      // `material.clearcoat > 0` at BUILD time, so on a MeshStandardMaterial the
      // field does not exist in the shader and a write to it is a GLSL error.
      // Left at 0 the slots emit nothing — which is every graph that existed
      // before this input did. `clearcoatRoughness` follows `clearcoat`, so a
      // roughness alone can never switch the lobe on.
      { handle: 'clearcoat',          type: 'float', default: 0, optInSlot: 'clearcoat' },
      { handle: 'clearcoatRoughness', type: 'float', default: 0, optInSlot: 'clearcoat' },
      // Anisotropy: the specular lobe stretched along a tangent direction —
      // brushed metal, and hair CARDS, where the strands run along the card and
      // an isotropic highlight reads as plastic. OPT-IN on the same rule as
      // clearcoat: three compiles it only under USE_ANISOTROPY, gated on
      // `material.anisotropy > 0` at BUILD time, so the slots emit nothing while
      // the strength is 0 and every older graph is byte-identical.
      // `anisotropyDirection` is the strand direction in TANGENT space — a card
      // tangent atlas decoded to -1..1, or any vector; only .xy is read, x along
      // the mesh tangent and y along the bitangent. Unwired, three's own default
      // (the tangent itself) stands, which is why its default is null.
      { handle: 'anisotropy',          type: 'float', default: 0,    optInSlot: 'anisotropy' },
      { handle: 'anisotropyDirection', type: 'vec3',  default: null, optInSlot: 'anisotropy' },
    ],
    outputs: [],
  },

  Fresnel: {
    label: 'Fresnel',
    inputs: [
      { handle: 'exponent', type: 'float', default: 5.0 },
      { handle: 'f0',       type: 'float', default: 0.04 },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
  },

  // Constants can be "Converted to Parameter" (Unreal-style): when node.data
  // .param is set, the value becomes a named uniform keyed by node.data.paramName
  // — two converted constants sharing a name+type are the SAME parameter.
  ConstantFloat: {
    label: 'Constant (float)',
    paramable: true,
    inputs: [{ handle: 'value', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
  },

  ConstantColor: {
    label: 'Constant (color)',
    paramable: true,
    inputs: [{ handle: 'value', type: 'vec3', default: [1, 1, 1] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  Multiply: {
    label: 'Multiply',
    inputs: [
      { handle: 'a', type: 'vec3', default: [1, 1, 1] },
      { handle: 'b', type: 'vec3', default: [1, 1, 1] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  Add: {
    label: 'Add',
    inputs: [
      { handle: 'a', type: 'vec3', default: [0, 0, 0] },
      { handle: 'b', type: 'vec3', default: [0, 0, 0] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  Lerp: {
    label: 'Lerp',
    inputs: [
      { handle: 'a',     type: 'vec3',  default: [0, 0, 0] },
      { handle: 'b',     type: 'vec3',  default: [1, 1, 1] },
      { handle: 'alpha', type: 'float', default: 0.5 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  Time: {
    label: 'Time',
    inputs: [],
    outputs: [{ handle: 'out', type: 'float' }],
  },

  // Scrolls UVs over time: out = uv + speed * time (Unreal's Panner). When `uv`
  // is unwired it defaults to the mesh UVs (vUv) — matching Unreal, where the
  // Coordinate input falls back to the mesh TextureCoordinate. (An inline [0,0]
  // default would scroll a single point, not the surface.) Feed `out` into a
  // Texture Sample / Noise `uv`.
  Panner: {
    label: 'Panner',
    inputs: [
      { handle: 'uv',    type: 'vec2',  default: null, usesMeshUv: true },
      { handle: 'speed', type: 'vec2',  default: [0.1, 0] },
    ],
    outputs: [{ handle: 'out', type: 'vec2' }],
  },

  TextureSample: {
    label: 'Texture Sample',
    // Texture binding lives on node.data.textureId (not a wired input). The node
    // renders a texture picker for it — see MaterialGraphNode. Convertible to a
    // texture parameter (Unreal TextureSampleParameter2D): same name = same
    // texture param (one shared sampler).
    hasTexture: true,
    paramable: true,
    // Blender-style color space (the image's "Color Space" setting): Color = sRGB
    // (albedo/emissive), Non-Color = linear data (roughness/AO/metalness/masks).
    // Default sRGB, matching Blender's default for a loaded image. Normals stay on
    // the dedicated Normal Map node — there's deliberately no "normal" mode here
    // (our `normal` slot doesn't do the tangent transform; the Normal Map node does).
    enums: [{
      key: 'colorSpace',
      default: 'srgb',
      options: [
        { value: 'srgb', label: 'Color (sRGB)' },
        { value: 'data', label: 'Non-Color (Data)' },
      ],
    }],
    inputs: [{ handle: 'uv', type: 'vec2', default: null, usesMeshUv: true }],
    outputs: [
      { handle: 'rgb', type: 'vec3' },
      { handle: 'a',   type: 'float' },
    ],
  },

  // UV / texture coordinates — the mesh's vUv. Drive Panner/TextureSample
  // explicitly, or transform before sampling. Flags needsUv in the compiler.
  // UV / texture coordinates. `channel` picks the mesh's UV set, Unreal's
  // TexCoord index: 0 is the first set (three's vUv, the default every sampler
  // uses when nothing is wired), 1 is the second (`uv1`), which the compiler
  // passes through as vGraphUv1 and flags needsUv1. MetaHuman hair-card meshes
  // carry their atlas coordinates in the SECOND set (the first spans exactly
  // 0..1 per card, so sampled with it every card shows the whole atlas), and a
  // lightmap or detail set is the ordinary reason a mesh has two.
  UV: {
    label: 'UV',
    enums: [{
      key: 'channel',
      default: 0,
      options: [
        { value: 0, label: 'UV 0' },
        { value: 1, label: 'UV 1' },
      ],
    }],
    inputs: [],
    outputs: [{ handle: 'out', type: 'vec2' }],
  },

  // Component Mask — Unreal-style. Swizzles the selected R/G/B channels of the
  // input; the output's arity is the number of checked channels (1 → float,
  // 2 → vec2, 3 → vec3). Channel toggles live on node.data.mask_{r,g,b}
  // (absent = on, so a fresh node passes all three through). Custom UI lives
  // in MaterialGraphNode (maskChannels).
  ComponentMask: {
    label: 'Component Mask',
    maskChannels: ['r', 'g', 'b'],
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // Split a vec3 into its three scalar channels.
  Split: {
    label: 'Split',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [
      { handle: 'r', type: 'float' },
      { handle: 'g', type: 'float' },
      { handle: 'b', type: 'float' },
    ],
  },

  // Combine three scalars into a vec3.
  Combine: {
    label: 'Combine',
    inputs: [
      { handle: 'r', type: 'float', default: 0 },
      { handle: 'g', type: 'float', default: 0 },
      { handle: 'b', type: 'float', default: 0 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  Subtract: {
    label: 'Subtract',
    inputs: [
      { handle: 'a', type: 'vec3', default: [0, 0, 0] },
      { handle: 'b', type: 'vec3', default: [0, 0, 0] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  Divide: {
    label: 'Divide',
    inputs: [
      { handle: 'a', type: 'vec3', default: [1, 1, 1] },
      { handle: 'b', type: 'vec3', default: [1, 1, 1] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  OneMinus: {
    label: 'One Minus',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  Saturate: {
    label: 'Saturate',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  Power: {
    label: 'Power',
    inputs: [
      { handle: 'base', type: 'vec3',  default: [1, 1, 1] },
      { handle: 'exp',  type: 'float', default: 2.0 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  Dot: {
    label: 'Dot',
    inputs: [
      { handle: 'a', type: 'vec3', default: [0, 0, 0] },
      { handle: 'b', type: 'vec3', default: [0, 0, 0] },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
  },

  Normalize: {
    label: 'Normalize',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 1] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  Sine: {
    label: 'Sine',
    inputs: [{ handle: 'in', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
  },

  // Color Ramp — map a 0..1 scalar through a gradient of color stops (Blender
  // ColorRamp). Stops live on node.data.stops = [{pos, color:[r,g,b]}, …]; the
  // node renders a gradient editor (see MaterialGraphNode). Compiles to a
  // piecewise linear mix() chain — stops baked as literals, no LUT/uniforms.
  ColorRamp: {
    label: 'Color Ramp',
    hasColorRamp: true,
    inputs: [{ handle: 'fac', type: 'float', default: 0 }],
    outputs: [{ handle: 'color', type: 'vec3' }],
  },

  // Step — hard threshold. out = value >= edge ? 1 : 0 (GLSL step(edge,value)).
  // Matches Unreal's Step and Blender's Math > Greater Than. Pair with Lerp for
  // a 2-tone cutout, or Smoothstep for a soft edge.
  Step: {
    label: 'Step',
    inputs: [
      { handle: 'edge',  type: 'float', default: 0.5 },
      { handle: 'value', type: 'float', default: 0 },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
  },

  // ── Tier-1 artist math/utility nodes (pure GLSL, no plumbing) ───────────────
  Smoothstep: {
    label: 'Smoothstep',
    inputs: [
      { handle: 'edge0', type: 'float', default: 0 },
      { handle: 'edge1', type: 'float', default: 1 },
      { handle: 'value', type: 'float', default: 0 },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
  },

  Clamp: {
    label: 'Clamp',
    inputs: [
      { handle: 'value', type: 'vec3',  default: [0, 0, 0] },
      { handle: 'min',   type: 'float', default: 0 },
      { handle: 'max',   type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // Map Range — remap value from [inMin,inMax] to [outMin,outMax] (Blender Map Range).
  Remap: {
    label: 'Remap',
    inputs: [
      { handle: 'value',  type: 'float', default: 0 },
      { handle: 'inMin',  type: 'float', default: 0 },
      { handle: 'inMax',  type: 'float', default: 1 },
      { handle: 'outMin', type: 'float', default: 0 },
      { handle: 'outMax', type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
  },

  Min: {
    label: 'Min',
    inputs: [{ handle: 'a', type: 'vec3', default: [0, 0, 0] }, { handle: 'b', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
  Max: {
    label: 'Max',
    inputs: [{ handle: 'a', type: 'vec3', default: [0, 0, 0] }, { handle: 'b', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
  Abs: {
    label: 'Abs',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
  Sign: {
    label: 'Sign',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
  Floor: {
    label: 'Floor',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
  Ceil: {
    label: 'Ceil',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
  Fract: {
    label: 'Fract',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
  Mod: {
    label: 'Mod',
    inputs: [{ handle: 'a', type: 'vec3', default: [0, 0, 0] }, { handle: 'b', type: 'float', default: 1 }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
  Cosine: {
    label: 'Cosine',
    inputs: [{ handle: 'in', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
  },
  Sqrt: {
    label: 'Sqrt',
    inputs: [{ handle: 'in', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
  },
  Distance: {
    label: 'Distance',
    inputs: [{ handle: 'a', type: 'vec3', default: [0, 0, 0] }, { handle: 'b', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'float' }],
  },
  Length: {
    label: 'Length',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'float' }],
  },
  Cross: {
    label: 'Cross',
    inputs: [{ handle: 'a', type: 'vec3', default: [1, 0, 0] }, { handle: 'b', type: 'vec3', default: [0, 1, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
  Reflect: {
    label: 'Reflect',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 1] }, { handle: 'normal', type: 'vec3', default: [0, 0, 1] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
  Luminance: {
    label: 'Luminance',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'float' }],
    // Rec.709 luma — RGB → grayscale (Blender "RGB to BW").
  },
  Gamma: {
    label: 'Gamma',
    inputs: [{ handle: 'in', type: 'vec3', default: [1, 1, 1] }, { handle: 'gamma', type: 'float', default: 2.2 }],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // ── Vector constructors / deconstructors ────────────────────────────────────
  // Combine (above) builds a vec3; these cover vec2 (e.g. assembling a custom
  // UV for Panner / Texture Sample) and vec4.
  MakeVector2: {
    label: 'Make Vector2',
    inputs: [
      { handle: 'x', type: 'float', default: 0 },
      { handle: 'y', type: 'float', default: 0 },
    ],
    outputs: [{ handle: 'out', type: 'vec2' }],
  },
  BreakVector2: {
    label: 'Break Vector2',
    inputs: [{ handle: 'in', type: 'vec2', default: [0, 0] }],
    outputs: [
      { handle: 'x', type: 'float' },
      { handle: 'y', type: 'float' },
    ],
  },
  MakeVector4: {
    label: 'Make Vector4',
    inputs: [
      { handle: 'x', type: 'float', default: 0 },
      { handle: 'y', type: 'float', default: 0 },
      { handle: 'z', type: 'float', default: 0 },
      { handle: 'w', type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'vec4' }],
  },

  // Rotate a vec2 (typically a UV) by `angle` radians around `center`. Pairs
  // with Time/Panner for spinning textures.
  Rotate2D: {
    label: 'Rotate2D',
    inputs: [
      { handle: 'uv',     type: 'vec2',  default: [0, 0] },
      { handle: 'angle',  type: 'float', default: 0 },
      { handle: 'center', type: 'vec2',  default: [0.5, 0.5] },
    ],
    outputs: [{ handle: 'out', type: 'vec2' }],
  },

  Tangent: {
    label: 'Tangent',
    inputs: [{ handle: 'in', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
  },
  ArcTan2: {
    label: 'ArcTan2',
    inputs: [
      { handle: 'y', type: 'float', default: 0 },
      { handle: 'x', type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
  },
  Exp: {
    label: 'Exp',
    inputs: [{ handle: 'in', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
  },
  Log: {
    label: 'Log',
    inputs: [{ handle: 'in', type: 'float', default: 1 }],
    outputs: [{ handle: 'out', type: 'float' }],
    // max() guards log(0)/log(negative) → -inf/NaN.
  },

  // Fractal noise, ported from Blender's Noise Texture node (all five fractal
  // types). `uv` defaults to the mesh UVs when unwired (inline editor hidden).
  //  • scale      — base frequency (bigger = finer features)
  //  • detail     — fractal octaves; Blender uses detail+1 octaves, fractional ok
  //  • roughness  — amplitude falloff per octave (strength of fine detail)
  //  • lacunarity — frequency multiplier per octave (usually 2)
  //  • distortion — domain warp applied to the coords before sampling
  //  • offset/gain — only used by Hetero Terrain / Ridged / Hybrid types
  // `type` picks the fractal: fBM / Multifractal / Hetero Terrain / Ridged /
  // Hybrid. `normalize` (fBM only) maps the result to [0,1]. Two outputs like
  // Blender: `fac` (float) and `color` (vec3 — three decorrelated channels).
  Noise: {
    label: 'Noise',
    enums: [{
      key: 'noiseType',
      default: 'fbm',
      options: [
        { value: 'fbm',          label: 'fBM' },
        { value: 'multifractal', label: 'Multifractal' },
        { value: 'hetero',       label: 'Hetero Terrain' },
        { value: 'ridged',       label: 'Ridged Multifractal' },
        { value: 'hybrid',       label: 'Hybrid Multifractal' },
      ],
    }],
    bools: [{ key: 'normalize', label: 'Normalize', default: true }],
    inputs: [
      { handle: 'uv',         type: 'vec2',  default: null, usesMeshUv: true },
      { handle: 'scale',      type: 'float', default: 5 },
      { handle: 'detail',     type: 'float', default: 2 },
      { handle: 'roughness',  type: 'float', default: 0.5 },
      { handle: 'lacunarity', type: 'float', default: 2 },
      { handle: 'distortion', type: 'float', default: 0 },
      { handle: 'offset',     type: 'float', default: 0 },
      { handle: 'gain',       type: 'float', default: 1 },
    ],
    outputs: [
      { handle: 'fac',   type: 'float' },
      { handle: 'color', type: 'vec3' },
    ],
  },

  // Bright/Contrast — Blender's node (gpu_shader_material_bright_contrast.glsl):
  // a = 1+contrast; b = bright - contrast*0.5; out = max(a*color + b, 0).
  // brightness is additive, contrast a slope. Both default 0 = no-op.
  BrightContrast: {
    label: 'Bright/Contrast',
    inputs: [
      { handle: 'color',    type: 'vec3',  default: [0, 0, 0] },
      { handle: 'bright',   type: 'float', default: 0 },
      { handle: 'contrast', type: 'float', default: 0 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // Hue/Saturation/Value — Blender's node (gpu_shader_material_hue_sat_val.glsl):
  // RGB→HSV, hue = fract(h + hue + 0.5) (so hue 0.5 = no shift), sat clamped,
  // value multiplied, HSV→RGB, then mix(color, result, fac). Defaults are no-op.
  HueSatVal: {
    label: 'Hue/Sat/Value',
    inputs: [
      { handle: 'hue',        type: 'float', default: 0.5 },
      { handle: 'saturation', type: 'float', default: 1 },
      { handle: 'value',      type: 'float', default: 1 },
      { handle: 'fac',        type: 'float', default: 1 },
      { handle: 'color',      type: 'vec3',  default: [0, 0, 0] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // Desaturation — Unreal's Desaturation expression: mix(color, luminance, fraction)
  // with Rec.601 luminance factors (0.3, 0.59, 0.11). fraction 0 = full colour,
  // 1 = full grey (default 1, matching Unreal's no-input-Fraction behaviour).
  Desaturation: {
    label: 'Desaturation',
    inputs: [
      { handle: 'color',    type: 'vec3',  default: [0, 0, 0] },
      { handle: 'fraction', type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // Normal Map — unpack a tangent-space normal texture and perturb the surface
  // normal. Texture loads LINEAR (normal maps aren't sRGB); the tangent frame is
  // derived per-fragment (aos_perturbNormal), so no precomputed tangents needed.
  // Wire `out` into the BSDF `normal` slot. Texture lives on node.data.textureId.
  NormalMap: {
    label: 'Normal Map',
    hasTexture: true,
    // Convertible to a texture parameter (like Texture Sample): same name = same
    // shared normal-map sampler. Always sampled linear regardless of param state.
    paramable: true,
    inputs: [
      { handle: 'uv',       type: 'vec2',  default: null, usesMeshUv: true },
      { handle: 'strength', type: 'float', default: 1 },
    ],
    // `out` is the perturbed SURFACE normal (wire into BSDF `normal`); `tangent`
    // is the decoded tangent-space vector with `strength` applied and nothing
    // else: the value Unreal's Normal input carries before the renderer
    // transforms it. It exists so two maps can be combined BEFORE perturbation
    // (Blend Angle Corrected Normals), which is where a detail normal has to be
    // blended: two perturbed surface normals averaged would halve both maps.
    outputs: [
      { handle: 'out',     type: 'vec3' },
      { handle: 'tangent', type: 'vec3' },
    ],
  },

  // Blend Angle Corrected Normals: Unreal's material function of the same
  // name, the same math (Reoriented Normal Mapping). The detail normal is
  // rotated onto the base normal rather than added to it, so a flat detail
  // leaves the base untouched and a flat base returns the detail unchanged.
  // Both inputs and the output are TANGENT-SPACE vectors (Normal Map's
  // `tangent` output, or another blend); feed the result to Perturb Normal.
  // A MetaHuman skin is exactly this: Normal_MAIN as the base, a tiled
  // micro-normal as the detail.
  BlendAngleCorrectedNormals: {
    label: 'Blend Angle Corrected Normals',
    inputs: [
      { handle: 'base',   type: 'vec3', default: [0, 0, 1] },
      { handle: 'detail', type: 'vec3', default: [0, 0, 1] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // Perturb Normal: takes a TANGENT-SPACE vector (Normal Map's `tangent`
  // output, or Blend Angle Corrected Normals) and turns it into the surface
  // normal the BSDF consumes, with the same per-fragment tangent frame Normal
  // Map uses internally. `uv` is the coordinate set that frame is derived
  // from and should be the mesh UV the BASE map was sampled with; it defaults
  // to exactly that, so it is rarely wired.
  PerturbNormal: {
    label: 'Perturb Normal',
    inputs: [
      { handle: 'tangent', type: 'vec3', default: [0, 0, 1] },
      { handle: 'uv',      type: 'vec2', default: null, usesMeshUv: true },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // World-space fragment position. Reads the vGraphWorldPos varying the builder
  // injects into the vertex shader (gated by compiler needsWorld). Useful for
  // triplanar mapping, world-space gradients, height-based effects.
  WorldPosition: {
    label: 'World Position',
    inputs: [],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // World-space surface normal (normalized). Reads vGraphWorldNormal. This is
  // the geometric normal — it does not include normal-map perturbation.
  WorldNormal: {
    label: 'World Normal',
    inputs: [],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // World-space view direction: unit vector from the fragment toward the camera.
  // Uses the built-in `cameraPosition` uniform + vGraphWorldPos. Feed into Dot
  // with World Normal for rim/fresnel-style effects.
  ViewDirection: {
    label: 'View Direction',
    inputs: [],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },

  // The mesh's tangent frame in world space (Unreal's VertexTangentWS), what a
  // view-dependent UV effect needs — an eye's iris refracting behind its cornea
  // is a parallax along the tangent and bitangent. The tangent comes from the
  // glTF TANGENT attribute, skinned with the mesh, and is
  // re-orthogonalised against the shading normal here; the bitangent follows
  // glTF's rule, cross(N, T) * w. A mesh with no tangent attribute reads the
  // attribute's default (0,0,0,1) and gets a frame built from its normal
  // instead, so the effect still runs, in a stable if arbitrary orientation.
  WorldTangent: {
    label: 'World Tangent',
    inputs: [],
    outputs: [
      { handle: 'tangent',   type: 'vec3' },
      { handle: 'bitangent', type: 'vec3' },
    ],
  },

  // Gaussian Color — the baked per-Gaussian color attribute of a Gaussian
  // splat. This is how a "plain" splat (one whose appearance is a baked RGB per
  // Gaussian, not a relit PBR surface) feeds its own colour into the BSDF: wire
  // this into the Principled BSDF `color` slot to pass the splat's stored colour
  // through, then layer tint / hue / contrast on top. Reads the vGaussianColor
  // varying the splat shading path supplies; in a mesh material (no such
  // attribute) the compiler declares a white fallback so the graph still
  // compiles. Flags needsSplatColor.
  GaussianColor: {
    label: 'Gaussian Color',
    inputs: [],
    outputs: [{ handle: 'out', type: 'vec3' }],
  },
}
